import Divider from 'components/Divider';
import { observer } from 'mobx-react-lite';

import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { useModal } from 'hooks/useModal';
import MissingServicesModal from '../../Locations/MissingServicesModal';
import { useAuthUser } from 'hooks/useAuthUser';
import { postQuote, updateOrder } from 'api/accessexpressv3/api';
import axios from 'axios';
import { postQuoteToRequest } from 'screens/AccessExpressV3/AccessProductDetails/utils';
import ClearAllModal from '../../ClearAllModal';
import { DIA_REFETCH_INTERVAL } from 'screens/AccessExpressV3/AccessProductDetails/AccessProductDetails';
import SaveAsDraft from '../../Basket/SaveAsDraft';
import { ProductCategory } from 'screens/AccessExpressV3/accessExpress.types';
import { UnassignedProducts } from '../../UnassignedProducts/UnassignedProducts';
import { ShippingAssignmentModal } from '../../UnassignedProducts/ShippingAssignmentModal';
import clsx from 'clsx';
import { buildItemPayload } from 'screens/AccessExpressV3/utils';

const LocationFooter = observer(() => {
  const cart = useCartContext();
  const navigate = useNavigate();
  const userInfo = useAuthUser();

  const { category: routeCategory } = useParams<{
    category: ProductCategory;
  }>();

  const goToAccessCheckout = (state?: {
    locationAssignmentPending?: boolean;
  }) => {
    navigate('/access-express/v3/checkout/access', { state });
  };

  const handleCheckout = () => {
    if (
      cart.unassignedPrimaryMobilityItems.length &&
      cart.shippingAssignmentMethod !== 'manual' &&
      cart.locations.length > 0
    ) {
      shippingAssignmentModalOpen();
      return;
    }

    if (cart.locationMissingServices.length) {
      missingServicesModalOpen();
      return;
    }

    goToAccessCheckout();
  };

  const { open: missingServicesModalOpen, ...missingServicesModalProps } =
    useModal();
  const { open: shippingAssignmentModalOpen, ...shippingAssignmentModalProps } =
    useModal();

  const onProceedToCheckout = async () => {
    cart.removeLocationsMissingServices();
    const dataForPostOrUpdate = {
      additional_details: cart.additionalDetails ?? {},
      locations: cart.locations.map((location) => {
        const order_items = cart
          .itemsByLocation(location.id)
          .filter((item) => item.category === 'Access')
          .map(buildItemPayload);
        return {
          address: {
            ...location,
            id: location.id as string,
          },
          order_items,
          id: location.id as string,
        };
      }),
    };

    try {
      const draft = await updateOrder(dataForPostOrUpdate, cart.draftId!);
      cart.updateStateFromDraft(draft);
    } catch (error) {
      console.error(error);
    }

    goToAccessCheckout();
  };

  const handleClearAll = () => {
    cart.clearAll();
  };

  const syncCartWithBackend = async () => {
    if (cart.draftId) {
      cart.setLoadingDraftQuote(true);
      // NOTE: This logic is duplicated across multiple components (handleLocationsChange, handleFindServices).
      const dataForPostOrUpdate = {
        additional_details:
          routeCategory === 'All' ? {} : cart.additionalDetails ?? {},
        locations: cart.locations.map((location) => {
          const order_items = cart
            .itemsByLocation(location.id)
            .filter((item) => item.category === 'Access')
            .map(buildItemPayload);
          return {
            address: {
              ...location,
              id: location.id as string,
            },
            order_items,
            id: location.id as string,
          };
        }),
      };
      const draft = await updateOrder(dataForPostOrUpdate, cart.draftId);
      cart.updateStateFromDraft(draft);

      if (!isAccessProductPage) {
        cart.setLoadingDraftQuote(false);
        return;
      }

      if (routeCategory === 'All') {
        return;
      }

      setTimeout(async () => {
        if (cart.selectedAccessProductCategory === 'DIA') {
          cart.setDIAFlowActive(true);
        }
        await postQuote(
          postQuoteToRequest(userInfo, cart.locations, [
            {
              product_type:
                cart.selectedAccessProductCategory as ProductCategory,
              ...cart.additionalDetails?.configs,
            },
          ]),
        )
          .then(async (quoteResponse) => {
            const additionalDetails = {
              configs: cart.additionalDetails?.configs,
              ...(cart.selectedAccessProductCategory === 'Starlink'
                ? { starlink_quote_request: quoteResponse.id }
                : cart.selectedAccessProductCategory === 'DIA'
                  ? { dia_quote_request: quoteResponse.id }
                  : { bb_quote_request: quoteResponse.id }),
            };
            await updateOrder(
              {
                additional_details: additionalDetails,
              },
              draft?.id ?? '',
            )
              .then((data) => cart.updateStateFromDraft(data))
              .finally(() => {
                if (cart.selectedAccessProductCategory === 'DIA') {
                  if (quoteResponse.status === 'Checking availability') {
                    setTimeout(() => {
                      cart.setStartPolling(true);
                    }, DIA_REFETCH_INTERVAL);
                  } else if (quoteResponse.status === 'Closed') {
                    cart.setLoadingDraftQuote(false);
                    cart.setStartPolling(false);
                  }
                } else {
                  cart.setLoadingDraftQuote(false);
                }
              });
          })
          .catch((error) => {
            if (axios?.isAxiosError(error) && error?.name === 'CanceledError')
              return;
            cart.setLoadingDraftQuote(false);
            console.log('Error creating quote:', error);
          });
      }, 0);
    }
  };

  const handleDone = () => {
    cart.doneEditingLocations();
    syncCartWithBackend();
  };

  const handleCancel = () => {
    cart.cancelEditingLocations();
  };

  const cartHasMobilityItems =
    cart.locations.length > 0 && cart.unassignedPrimaryMobilityItems.length > 0;

  const onKeepShopping = () => {
    navigate(-1);
    cart.close();
  };

  const isAccessProductPage = location.pathname.includes('/access/product');
  const isMobilityProductDetailsPage =
    location.pathname.includes('/product') && !isAccessProductPage;

  const handleShippingAssignmentConfirmation = (
    method: 'checkout' | 'manual',
  ) => {
    cart.setShippingAssignmentMethod(method);
    shippingAssignmentModalProps.close();

    if (method === 'checkout') {
      goToAccessCheckout({ locationAssignmentPending: true });
    }
  };

  return (
    <div className="flex flex-col gap-8">
      {cart.isEditingLocations ? (
        <div className="flex gap-4">
          <GraniteButton
            variant="secondary"
            className="w-full"
            onClick={handleCancel}
          >
            Cancel
          </GraniteButton>
          <GraniteButton
            variant="tertiary"
            className="w-full"
            onClick={handleDone}
            disabled={cart.locations.length === 0}
          >
            Done
          </GraniteButton>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-8">
            {cart.shippingAssignmentMethod === 'manual' && (
              <Divider className="" />
            )}
            {cartHasMobilityItems && <UnassignedProducts />}
            {cart.shippingAssignmentMethod !== 'manual' && (
              <div className="flex flex-col gap-2">
                <Divider />
                <div className="flex justify-between">
                  <GraniteButton
                    size="small"
                    variant="ghost"
                    className={clsx(
                      'hover:!border-accent-primary hover:!text-accent-primary',
                      {
                        invisible:
                          isMobilityProductDetailsPage &&
                          cart.locations.length === 0,
                      },
                    )}
                    onClick={() => cart.editLocations()}
                  >
                    Edit locations
                  </GraniteButton>
                  <ClearAllModal
                    onClick={handleClearAll}
                    disabled={cart.items.length === 0}
                  />
                </div>
              </div>
            )}
          </div>
          {!cart.resumeOrder?.id || cart.totalItems !== 0 ? (
            <div className="flex flex-col gap-4">
              {cart.totalItems !== 0 && (
                <GraniteButton
                  variant="tertiary"
                  onClick={handleCheckout}
                  disabled={
                    cart.loadingDraftForPricing || cart.loadingDraftQuote
                  }
                >
                  Checkout ({cart.totalQuantity})
                </GraniteButton>
              )}
              {cart.shippingAssignmentMethod !== 'manual' ? (
                <div className="flex justify-between gap-4">
                  <SaveAsDraft variant="secondary" />
                  {isMobilityProductDetailsPage && (
                    <GraniteButton
                      variant="secondary"
                      className="w-full"
                      onClick={onKeepShopping}
                    >
                      Keep shopping
                    </GraniteButton>
                  )}
                </div>
              ) : (
                <GraniteButton
                  variant="secondary"
                  onClick={() => cart.resetShippingAssignmentMethod()}
                >
                  Back
                </GraniteButton>
              )}
            </div>
          ) : null}
          <MissingServicesModal
            {...missingServicesModalProps}
            onProceedToCheckout={onProceedToCheckout}
          />
          <ShippingAssignmentModal
            {...shippingAssignmentModalProps}
            onConfirmation={handleShippingAssignmentConfirmation}
          />
        </>
      )}
    </div>
  );
});

export default LocationFooter;
